import {
	TUTORIAL_CASE_ID,
	caseDetail,
	collectionResume,
	contractsList,
	documentTypes,
	occurrenceLimits,
	statusTrackLogs,
} from './tutorialFixtures';

/**
 * De que requisição sai qual resposta do roteiro.
 *
 * Cada entrada é um teste explícito sobre método e caminho. Não há regra genérica do
 * tipo "todo GET de /cases responde X" de propósito: o roteiro precisa ser uma lista
 * que dá para ler e conferir, porque é ela que decide o que NÃO sai daqui.
 */

interface Route {
	method: string;
	matches: (url: string, params?: unknown) => boolean;
	body: unknown;
}

/** A URL chega relativa à baseURL, com ou sem barra inicial. */
function path(url: string): string {
	// O `?? ''` não é defensividade vazia: `split` é tipado como podendo devolver vazio,
	// e sem ele o arquivo não passa na checagem de tipo.
	const withoutQuery = ('/' + url.replace(/^\/+/, '')).split('?')[0] ?? '';

	return withoutQuery.replace(/\/+$/, '');
}

const ROUTES: Route[] = [
	{
		method: 'get',
		matches: (url) => path(url) === '/v2/cases',
		body: contractsList,
	},
	{
		method: 'get',
		// A tela pede sempre o mesmo contrato do roteiro; qualquer outro id cairia aqui
		// também, e responder o mesmo é melhor do que deixar a tela em esqueleto eterno.
		matches: (url) => /^\/cases\/[^/]+$/.test(path(url)),
		body: caseDetail,
	},
	{
		method: 'get',
		matches: (url) => path(url) === `/cases/${TUTORIAL_CASE_ID}/tracking`
			|| /^\/cases\/[^/]+\/tracking$/.test(path(url)),
		body: statusTrackLogs,
	},
	{
		method: 'get',
		matches: (url) => /^\/quotation\/occurrence-limits\/[^/]+$/.test(path(url)),
		body: occurrenceLimits,
	},
	{
		method: 'get',
		matches: (url) => /^\/cases\/[^/]+\/collection-resume$/.test(path(url)),
		body: collectionResume,
	},
	{
		method: 'get',
		matches: (url) => path(url) === '/services/enum/DocumentType',
		body: documentTypes,
	},

	// Listas que a tela de contratos carrega e que, vazias, não atrapalham o tutorial:
	// servem para preencher filtros que a pessoa não vai usar aqui.
	{
		method: 'get',
		matches: (url) => path(url) === '/brokers' || path(url) === '/product/visible',
		body: { data: [], count: 0, sumarized: null },
	},
	{
		method: 'get',
		matches: (url) => path(url).startsWith('/Parameters/'),
		body: { data: { isBlocked: false } },
	},
	{
		method: 'get',
		matches: (url) => /^\/cases\/[^/]+\/(occurrence-reviews|financial-statement)$/.test(path(url)),
		body: { data: [] },
	},
	{
		method: 'get',
		matches: (url) => path(url).startsWith('/ExonerationRecommendations'),
		body: { data: [] },
	},

	/**
	 * A criação da ocorrência.
	 *
	 * Esta é a única escrita prevista, e ela responde sucesso SEM sair daqui. É o
	 * ponto inteiro do tutorial: a pessoa percorre o fluxo até o fim, vê a mensagem de
	 * sucesso que veria de verdade, e nada é criado.
	 */
	{
		method: 'post',
		matches: (url) => path(url) === '/cases/create-with-files',
		body: { data: { id: TUTORIAL_CASE_ID } },
	},
];

export function resolveFixture(method: string, url: string, params?: unknown): unknown | null {
	const route = ROUTES.find((r) => r.method === method && r.matches(url, params));

	return route ? route.body : null;
}
